import { Box, Button, Divider, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { MapContext } from "contexts/StateContext";
import { codeIntoCoord, coordsIntoCode } from "utils/coordinatesConvert";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import OSNewMapContainer from "./OSNewMapContainer";

const NewAddressForm = ({
  addressDetail = null,
  isBusy = false,
  onSubmit = () => {},
  addressCode,
  coordinates,
  onCancel = () => {},
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      code: addressCode || "",
      coordinates: coordinates ? `${coordinates.lat}, ${coordinates.lng}` : "",
      uniqueName: "",
      reference: "",
      description: "",
    },
  });

  const [searchLocation, setSearchLocation] = useState(null);
  const [selectedLocation] = useState(null);

  const { searchCoords } =
    React.useContext(MapContext);

  useEffect(() => {
    setSearchLocation(searchCoords);
  }, [searchCoords]);

  const [searchParams] = useSearchParams();
  const code = searchParams.get("code") || "";

  useEffect(() => {
    if (!code) return;
    setSearchLocation(codeIntoCoord(code));
  }, [code]);
  const [initialValue, setInitialValue] = useState("");

  useEffect(() => {
    if (addressDetail) {
      reset({
        ...addressDetail,
        coordinates: addressDetail.coordinates
          ? `${addressDetail.coordinates.lat}, ${addressDetail.coordinates.lng}`
          : "",
      });
    }
  }, [addressDetail, reset]);

  const generateRandomUUID = () => {
    const randomDigits = Math.floor(1000000000 + Math.random() * 9000000000);
    return `CAN-ADDRESS-${randomDigits}`;
  };

  useEffect(() => {
    if (addressDetail && addressDetail.uniqueName) {
      setInitialValue(addressDetail.uniqueName);
      setValue("uniqueName", addressDetail.uniqueName);
    } else {
      const uuid = generateRandomUUID();
      setInitialValue(uuid);
      setValue("uniqueName", uuid);
    }
  }, [addressDetail, setValue]);

  const handleCoordinateChange = (value) => {
    const [lat, lng] = value.split(",").map(parseFloat);
    if (!isNaN(lat) && !isNaN(lng)) {
      const calculatedCode = coordsIntoCode({ lat, lng });
      setValue("code", calculatedCode);
    }
  };

  const handleOnAddressSubmit = (data) => {
    const { coordinates, ...rest } = data;
    const [lat, lng] = coordinates.split(",").map(parseFloat);

    const payload = {
      coordinates: { lat, lng },
      ...rest,
    };
    onSubmit(payload);
  };

  const renderTextField = (name, label, options = {}, additionalProps = {}) => (
    <Controller
      name={name}
      control={control}
      rules={options}
      render={({ field }) => (
        <TextField
          fullWidth
          label={label}
          size="small"
          error={!!errors[name]}
          helperText={errors[name]?.message}
          {...field}
          {...additionalProps}
        />
      )}
    />
  );

  return (
    <LoadingWrapper loading={isBusy}>
      <Grid
        container
        spacing={2}
        style={{
          height: "70vh",
          alignItems: "stretch",
        }}
      >
        {/* Left Column: Form */}
        <Grid
          item
          xs={12}
          md={4}
          style={{
            padding: 4,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box padding={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="coordinates"
                  control={control}
                  rules={{
                    required: "Coordinates are required",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="text"
                      label="Coordinates"
                      placeholder="28.937657, 70.350585"
                      size="small"
                      error={!!errors.coordinates}
                      helperText={errors.coordinates?.message}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value);
                        handleCoordinateChange(value);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "code",
                  "Address Code",
                  { required: "Address Code is required" },
                  {
                    InputProps: { readOnly: true },
                  }
                )}
              </Grid>

              <Grid item xs={12}>
                <Controller
                  name="uniqueName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location Name"
                      value={field.value || initialValue}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        style: { textTransform: "uppercase" },
                      }}
                      onChange={(e) => {
                        const input = e.target.value;
                        const transformedValue = input.toUpperCase();
                        setInitialValue(transformedValue);
                        setValue("uniqueName", transformedValue);
                        field.onChange(transformedValue);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                {renderTextField("reference", "Reference")}
              </Grid>

              <Grid item xs={12}>
                {renderTextField(
                  "description",
                  "Description",
                  {},
                  { multiline: true, rows: 4 }
                )}
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box
            paddingX={4}
            paddingY={2}
            display="flex"
            justifyContent="flex-end"
            gap={2}
          >
            <Button disabled={isBusy} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={isBusy}
              onClick={handleSubmit(handleOnAddressSubmit)}
            >
              {addressDetail ? "Update" : "Add"}
            </Button>
          </Box>
        </Grid>
        {/* Right Column: Map */}
        <Grid
          item
          xs={12}
          md={8}
          style={{
            display: "flex",
            alignItems: "stretch",
          }}
        >
          <Box
            style={{
              width: "100%",
              height: "97%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: -6,
            }}
          >
            <div
              className="h-full w-[810px] flex-shrink-0 overflow-y-auto p-2"
              style={{
                borderColor: "gray",
                backgroundColor:
                  useTheme().palette.mode === "dark" ? "#121212" : "#ffffff",
                color:
                  useTheme().palette.mode === "dark" ? "#ffffff" : "#000000",
              }}
            >
              {/* Replace with your Map Component */}
              <OSNewMapContainer
                {...{ searchLocation, selectedLocation }}
                handleFormData={(data) => {
                  setValue("coordinates", data);
                  handleCoordinateChange(data) ;
                }
                }
              />
            </div>
          </Box>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default NewAddressForm;
